import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import {
  Validators,
  FormControl,
  FormGroup,
  FormBuilder,
} from '@angular/forms';
import { UserService } from '../../../services/user.service';
import { CyclistService } from '../../../services/cyclist.service';
import { BikeService } from '../../../services/bike.service';
import { PartnerService } from '../../../services/partner.service';
import { LookupService } from '../../../services/lookup.service';
import { AppConfig } from '../../../app.config';

import { OverlayPanel } from 'primeng/overlaypanel';
import { ConfirmationService } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HelpersService } from '../../../services/helpers.service';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-cyclist-bike',
  templateUrl: './cyclist-bike.component.html',
  styleUrls: ['./cyclist-bike.component.scss'],
})
export class CyclistBikeComponent implements OnInit {
  @Input() user;
  @Input() partnerId;
  @Input() email;
  @Input() messageService: MessageService;
  @Output() update = new EventEmitter();

  public UserForm: FormGroup;
  public genders: any = [
    { label: this.translate.instant('general.male'), value: 1 },
    { label: this.translate.instant('general.female'), value: 2 },
  ];
  public busy: boolean = false;
  public error: boolean = false;
  public errorMessage = '';
  public userExists: boolean = false;
  public userLinkedToPartner: boolean = false;
  public manufacturers: any = [];
  public models: any = [];
  public years: any = [];
  public DefaultUnitOfMeasure = 1;
  public partnerData: any = {};

  constructor(
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    public userService: UserService,
    public helpersService: HelpersService,
    public cyclistService: CyclistService,
    public partnerService: PartnerService,
    public bikeService: BikeService,
    private lookupService: LookupService,
    public modal: NgbActiveModal,
    public appConfig: AppConfig,
    private translate: TranslateService,
  ) {
    this.partnerData = helpersService.getPartnerData();
    this.DefaultUnitOfMeasure = this.partnerData.DefaultUnitOfMeasure;
  }

  ngOnInit() {
    this.setupValidation();
  }

  setupValidation() {
    this.UserForm = this.fb.group({
      Name: new FormControl(
        this.user.Name,
        this.user && this.user.ID
          ? Validators.nullValidator
          : Validators.required,
      ),
      Surname: new FormControl(
        this.user.Surname,
        this.user && this.user.ID
          ? Validators.nullValidator
          : Validators.required,
      ),
      Email: new FormControl(
        this.user.ID && this.user.Email
          ? this.user.Email.trim()
          : this.email && this.email.indexOf('@') > -1
            ? this.email.trim()
            : '',
        this.user && this.user.ID
          ? Validators.nullValidator
          : Validators.compose([Validators.nullValidator, Validators.email]),
      ),
      PhoneNumber: new FormControl(
        this.user.PhoneNumber,
        Validators.nullValidator,
      ),
      //'Email': new FormControl(this.user.ID ? this.user.Email : (this.email && this.email.indexOf("@") > -1 ? this.email : ""), Validators.compose([Validators.required, Validators.email])),
      //'PhoneNumber': new FormControl(this.user.PhoneNumber, Validators.nullValidator),

      Manufacturer: new FormControl(
        this.user.Manufacturer,
        Validators.required,
      ),
      Model: new FormControl(this.user.Model, Validators.required),
      Color: new FormControl(this.user.Color, Validators.nullValidator),
      Year: new FormControl(this.user.Year, Validators.nullValidator),
      SerialNo: new FormControl(this.user.SerialNo, Validators.nullValidator),
    });
  }

  reloadControlData() {
    this.UserForm.controls['Name'].setValue(this.user.Name);
    this.UserForm.controls['Surname'].setValue(this.user.Surname);
    this.UserForm.controls['PhoneNumber'].setValue(this.user.PhoneNumber);
    this.UserForm.controls['DateOfBirth'].setValue(
      moment(this.user.DateOfBirth).toDate(),
    );
    this.UserForm.controls['GenderID'].setValue(this.user.GenderID);
  }

  disableControls() {
    this.UserForm.controls['Name'].disable();
    this.UserForm.controls['Surname'].disable();
    this.UserForm.controls['PhoneNumber'].disable();
    this.UserForm.controls['DateOfBirth'].disable();
    this.UserForm.controls['GenderID'].disable();
  }

  enableControls() {
    this.UserForm.controls['Name'].enable();
    this.UserForm.controls['Surname'].enable();
    this.UserForm.controls['PhoneNumber'].enable();
    this.UserForm.controls['DateOfBirth'].enable();
    this.UserForm.controls['GenderID'].enable();
  }

  searchManufacturer(event) {
    this.lookupService
      .getManufacturerLookup(event.query)
      .subscribe((manufacturers) => {
        this.manufacturers = manufacturers.map((item) => {
          return item.Description;
        });
      });
  }

  searchModel(event) {
    this.lookupService
      .getModelLookup(this.UserForm.controls['Manufacturer'].value, event.query)
      .subscribe((models) => {
        this.models = models.map((item) => {
          return item.Description;
        });
      });
  }

  searchYear(event) {
    this.lookupService
      .getYearLookup(
        this.UserForm.controls['Manufacturer'].value,
        this.UserForm.controls['Model'].value,
        event.query,
      )
      .subscribe((years) => {
        this.years = years.map((item) => {
          return item.Description;
        });
      });
  }

  searchUser(event) {
    if (this.UserForm.controls['Email'].value.length > 0) {
      this.userExists = false;
      this.cyclistService
        .searchCyclist(this.partnerId, this.UserForm.controls['Email'].value)
        .subscribe((cyclists) => {
          if (cyclists && cyclists.length > 0) {
            this.userExists = true;
            //The schedule service has a call back to handle when a user is found.
            if (this.update.observers.length > 0) {
              this.update.emit({ searchAgain: true, cyclist: cyclists[0] });
            } else {
              //Lock form as this is a new cyclist form.
              this.confirmationService.confirm({
                message: this.translate.instant(
                  'messages.user_with_email_exists',
                ),
                acceptLabel: this.translate.instant('general.ok'),
                rejectVisible: false,
              });
            }
          }
        });
    }
  }

  searchUserPhone(event) {
    if (this.UserForm.controls['PhoneNumber'].value.length > 0) {
      this.userExists = false;
      this.cyclistService
        .searchCyclist(
          this.partnerId,
          this.UserForm.controls['PhoneNumber'].value,
        )
        .subscribe((cyclists) => {
          if (cyclists && cyclists.length > 0) {
            this.userExists = true;
            //The schedule service has a call back to handle when a user is found.
            if (this.update.observers.length > 0) {
              this.update.emit({ searchAgain: true, cyclist: cyclists[0] });
            } else {
              //Lock form as this is a new cyclist form.
              this.confirmationService.confirm({
                message: this.translate.instant(
                  'messages.user_with_phone_number_exists',
                ),
                acceptLabel: this.translate.instant('general.ok'),
                rejectVisible: false,
              });
            }
          }
        });
    }
  }

  valid() {
    return (
      (this.UserForm.controls['Email'].value &&
        this.UserForm.controls['Email'].value.length > 0) ||
      (this.UserForm.controls['PhoneNumber'].value &&
        this.UserForm.controls['PhoneNumber'].value.length > 0)
    );
  }

  saveUser(formData) {
    if (this.user.ID) {
      this.createBike(formData, this.user);
    } else {
      this.createUser(formData);
    }
  }

  createUser(formData) {
    let user = {
      ...formData,
      ...{
        Username: formData.Email ? formData.Email : formData.PhoneNumber,
        Password: this.appConfig.defaultUserPassword(),
        ConfirmPassword: this.appConfig.defaultUserPassword(),
        PartnerID: 0,
      },
    };
    this.busy = true;
    this.error = false;
    user.PartnerID = this.partnerId;
    this.userService.createCustomer(user).subscribe(
      (newUser) => {
        //Set the users default unit of measure... need to change the registerUser API so we can pass this there...
        //this.cyclistService.getCyclistById(newUser.Data.ID).subscribe((cyclist) => {
        //  cyclist.UnitsOfMeasureID = this.DefaultUnitOfMeasure;
        //  cyclist.PartnerID = this.partnerId;
        //  this.cyclistService.saveCyclist(cyclist).subscribe((user) => { });
        //});
        //this.partnerService
        //  .linkCyclistToPartner(this.partnerId, newUser.ID)
        //  .subscribe(
        //    (linked) => {
        //      newUser.PartnerID = this.partnerId;
        //      this.cyclistService.linkCyclists(this.partnerId, newUser).subscribe(linkedUser => {
        //        if (formData.Manufacturer) {
        //          this.createBike(formData, newUser);
        //        } else {
        //          if (this.update.observers.length > 0) {
        //            this.update.emit({ bike: null, cyclist: user });
        //          } else {
        //            this.messageService.add({
        //              life: 5000,
        //              detail: this.translate.instant('messages.cyclist_created'),
        //            });
                    this.modal.close();
        //          }
        //        }
        //      });
        //    },
        //    (error) => {
        //      this.busy = false;
        //    },
        //  );
      },
      (error) => {
        this.busy = false;
        this.errorMessage = this.translate.instant(
          'messages.problem_creating_cyclist',
        );
        this.error = true;
        this.confirmationService.confirm({
          message: this.translate.instant('messages.problem_creating_cyclist'),
          acceptLabel: this.translate.instant('general.ok'),
          rejectVisible: false,
        });
      },
    );
  }

  createBike(formData, user) {
    this.busy = true;
    let bike = {
      ID: 0,
      TypeID: 0,
      UserID: user.ID,
      RefNo: formData.Manufacturer + ' - ' + formData.Model,
      SerialNo: formData.SerialNo,
      Manufacturer: formData.Manufacturer,
      Model: formData.Model,
      Colour: formData.Color,
      ModelYear: formData.Year,
      LastService: new Date(),
      DateBought: null,
      StatusID: 0,
      PartnerType: this.partnerData.PartnerType,
    };
    this.bikeService.saveBike(bike).subscribe(
      (newBike) => {
        this.busy = false;
        if (this.update.observers.length > 0) {
          this.update.emit({ bike: newBike, cyclist: user });
        } else {
          this.modal.close();
          this.messageService.add({
            life: 5000,
            detail: this.translate.instant('messages.cyclist_bike_created'),
          });
        }
      },
      (error) => {
        this.busy = false;
      },
    );
  }
}

